import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  TextField,
  MenuItem,
  InputAdornment,
  Paper,
  Chip,
  Button,
  Divider,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import UserEditForm from '../components/UserEditForm';
import UserCreateForm from '../components/UserCreateForm';
import { DataTable, StatusBadge } from '../components/ui';

const UserListPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Filters and search
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, roleFilter, statusFilter, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page,
        limit: pageSize
      });

      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());

      const response = await api.get(`/users?${params}`);
      setUsers(response.data.data);
      setTotalUsers(response.data.pagination.total);
      setError('');
    } catch (err) {
      setError(t('admin:users.messages.fetchError'));
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (userToEdit) => {
    setSelectedUser(userToEdit);
    setOpenEditDialog(true);
  };

  const handleForceLogout = async (userToLogout) => {
    try {
      await api.post(`/users/${userToLogout._id}/force-logout`);
      setSuccess(t('admin:users.messages.forceLogoutSuccess'));
    } catch (err) {
      setError(t('admin:users.messages.forceLogoutError'));
      console.error('Error forcing logout:', err);
    }
  };

  const handleFormSubmit = async (userData) => {
    try {
      const response = await api.put(`/users/${selectedUser._id}`, userData);

      // Check if next plan was cleared due to instant activation
      if (response.data.nextPlanCleared) {
        setSuccess(t('admin:users.messages.updateSuccessNextPlanCleared',
          'User updated successfully. Queued next plan was cancelled to prevent overwriting this assignment.'));
      } else {
        setSuccess(t('admin:users.messages.updateSuccess'));
      }

      setOpenEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(t('admin:users.messages.updateError'));
      console.error('Error updating user:', err);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      await api.post('/users', userData);
      setSuccess(t('admin:createUser.messages.createSuccess'));
      setOpenCreateDialog(false);
      fetchUsers();
    } catch (err) {
      // Re-throw the error so the form can handle it
      throw err;
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleStatus = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'trainer':
        return 'warning';
      case 'member':
        return 'success';
      default:
        return 'default';
    }
  };

  // Define table columns
  const columns = [
    {
      key: 'name',
      title: t('admin:users.table.headers.name'),
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            src={row.profilePhotoThumbnail || row.profilePhoto}
            sx={{ width: 36, height: 36, mr: 1.5, bgcolor: stringToColor(`${row.firstName} ${row.lastName}`) }}
          >
            {getInitials(`${row.firstName} ${row.lastName}`)}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 'bold' }}>
              {row.firstName} {row.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.username}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      key: 'email',
      title: t('admin:users.table.headers.email'),
      sortable: true,
      filterable: true,
      render: (value) => (
        <Tooltip title={`Send email to ${value}`}>
          <Typography 
            variant="body2" 
            sx={{ 
              textDecoration: 'underline', 
              color: 'primary.main',
              '&:hover': { cursor: 'pointer' }
            }}
            onClick={() => window.open(`mailto:${value}`)}
          >
            {value}
          </Typography>
        </Tooltip>
      )
    },
    {
      key: 'role',
      title: t('admin:users.table.headers.role'),
      sortable: true,
      render: (value, row) => (
        <StatusBadge status={getRoleStatus(row.role)}>
          {row.role.charAt(0).toUpperCase() + row.role.slice(1)}
        </StatusBadge>
      )
    },
    {
      key: 'membershipPlan',
      title: t('admin:users.table.headers.membershipPlan'),
      render: (value, row) => (
        row.membershipPlan ? (
          <Tooltip title={`$${row.membershipPlan.price} for ${row.membershipPlan.duration} days`}>
            <Box>
              <StatusBadge status="info" variant="outline">
                {row.membershipPlan.name}
              </StatusBadge>
            </Box>
          </Tooltip>
        ) : (
          <Chip 
            label={t('admin:users.table.noPlan')} 
            size="small" 
            variant="outlined" 
            color="default"
            sx={{ bgcolor: 'background.paper' }}
          />
        )
      )
    },
    {
      key: 'isActive',
      title: t('admin:users.table.headers.status'),
      sortable: true,
      render: (value, row) => (
        <StatusBadge status={row.isActive ? 'success' : 'error'}>
          {row.isActive ? t('admin:users.table.status.active') : t('admin:users.table.status.inactive')}
        </StatusBadge>
      )
    },
    {
      key: 'createdAt',
      title: t('admin:users.table.headers.joined'),
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2">
            {formatDate(row.createdAt)}
          </Typography>
          <Chip 
            size="small" 
            label={getJoinedDuration(row.createdAt)} 
            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
            color={getJoinedDurationColor(row.createdAt)}
          />
        </Box>
      )
    }
  ];
  
  // Helper function to generate consistent colors from strings
  const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };
  
  // Helper function to get initials from name
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };
  
  // Helper function to get joined duration text
  const getJoinedDuration = (dateString) => {
    const joinedDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - joinedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return `${diffDays}d`;
    if (diffDays < 365) return `${Math.floor(diffDays/30)}m`;
    return `${Math.floor(diffDays/365)}y`;
  };
  
  // Helper function to get color based on join date
  const getJoinedDurationColor = (dateString) => {
    const joinedDate = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - joinedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) return 'success';
    if (diffDays < 180) return 'info';
    return 'default';
  };

  // Define table actions (admins only). Receptionists get read-only view.
  const actions = React.useMemo(() => {
    if (!user || user.role !== 'admin') {
      return { row: [] };
    }
    return {
      row: [
        {
          icon: <EditIcon />,
          title: t('admin:users.actions.editUser'),
          onClick: handleEditUser,
          variant: 'ghost'
        },
        {
          icon: <LogoutIcon />,
          title: t('admin:users.actions.forceLogout'),
          onClick: handleForceLogout,
          variant: 'warning',
          disabled: (row) => row._id === user._id
        }
      ]
    };
  }, [user, handleForceLogout]);

  // Define pagination config
  const paginationConfig = {
    page: page,
    pageSize: pageSize,
    total: totalUsers,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSizeOptions: [5, 10, 25, 50]
  };

  // Define empty state
  const emptyState = (
    <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
      <Avatar 
        sx={{ 
          width: 80, 
          height: 80, 
          mb: 2, 
          mx: 'auto',
          bgcolor: 'background.default',
          border: '2px dashed',
          borderColor: 'divider'
        }}
      >
        <PersonIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      </Avatar>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 'medium' }}>
        {t('admin:users.emptyState.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
        {t('admin:users.emptyState.description')}
      </Typography>
      <Button 
        variant="outlined" 
        onClick={() => {
          setSearchTerm('');
          setRoleFilter('all');
          setStatusFilter('all');
        }}
      >
{t('admin:users.emptyState.clearFilters')}
      </Button>
    </Box>
  );

  // Allow admin (full) and receptionist (read-only)
  const isAdmin = user?.role === 'admin';
  const isReceptionist = user?.role === 'receptionist';
  if (!isAdmin && !isReceptionist) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('admin:users.accessDenied')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <PersonIcon />
          </Avatar>
          <Typography variant="h4" component="h1">
            {t('admin:users.title')}
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
          >
            {t('admin:users.actions.addUser')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight="medium">{t('admin:users.filters.title')}</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label={t('admin:users.filters.searchLabel')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ minWidth: 120, maxWidth:240, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            placeholder={t('admin:users.filters.searchPlaceholder')}
          />
          
          <TextField
            select
            label={t('admin:users.filters.role')}
            value={roleFilter}
            onChange={handleRoleFilterChange}
            size="small"
            sx={{ minWidth: 120 , maxWidth:240 }}
          >
            <MenuItem value="all">{t('admin:users.filters.allRoles')}</MenuItem>
            <MenuItem value="member">{t('admin:users.filters.members')}</MenuItem>
            <MenuItem value="trainer">{t('admin:users.filters.trainers')}</MenuItem>
            <MenuItem value="admin">{t('admin:users.filters.admins')}</MenuItem>
          </TextField>

          <TextField
            select
            label={t('admin:users.filters.status')}
            value={statusFilter}
            onChange={handleStatusFilterChange}
            size="small"
            sx={{ minWidth: 120, maxWidth:240 }}
          >
            <MenuItem value="all">{t('admin:users.filters.allStatus')}</MenuItem>
            <MenuItem value="active">{t('admin:users.filters.active')}</MenuItem>
            <MenuItem value="inactive">{t('admin:users.filters.inactive')}</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 2 }}>
        <DataTable
          data={users}
          columns={columns}
          loading={loading}
          error={error}
          pagination={paginationConfig}
          sorting={{ enabled: true }}
          filtering={{ enabled: true }}
          actions={actions}
          emptyState={emptyState}
          sx={{
            '& .MuiTableRow-root:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiTableCell-head': {
              backgroundColor: 'background.paper',
              fontWeight: 'bold',
            }
          }}
        />
      </Paper>

      {/* Summary Stats */}
      {!loading && users.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={t('admin:users.stats.totalUsers', { count: totalUsers })} 
            color="primary" 
            variant="outlined" 
          />
          <Chip 
            label={t('admin:users.stats.active', { count: users.filter(u => u.isActive).length })} 
            color="success" 
            variant="outlined" 
          />
          <Chip 
            label={t('admin:users.stats.inactive', { count: users.filter(u => !u.isActive).length })} 
            color="error" 
            variant="outlined" 
          />
        </Box>
      )}

      {/* User Edit Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              src={selectedUser?.profilePhotoThumbnail || selectedUser?.profilePhoto}
              sx={{ mr: 2, bgcolor: selectedUser ? stringToColor(`${selectedUser.firstName} ${selectedUser.lastName}`) : 'primary.main' }}
            >
              {selectedUser ? getInitials(`${selectedUser.firstName} ${selectedUser.lastName}`) : <PersonIcon />}
            </Avatar>
            <Typography variant="h6">
              {t('admin:users.editDialog.title', { name: `${selectedUser?.firstName} ${selectedUser?.lastName}` })}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <UserEditForm
            user={selectedUser}
            onSubmit={handleFormSubmit}
            onCancel={() => setOpenEditDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* User Create Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
              <AddIcon />
            </Avatar>
            <Typography variant="h6">
              {t('admin:createUser.title')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <UserCreateForm
            onSubmit={handleCreateUser}
            onCancel={() => setOpenCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserListPage;
